import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { Formik, Form } from 'formik';
import moment from 'moment';

import { UTILITY_USER_ROLE_ADMIN, hasRoleAdmin } from 'common/utility/userRoles';

import {
  UTILITY_PARTNER_STATUS_PENDING_DEMO,
  UTILITY_PARTNER_STATUS_ONBOARDED,
  UTILITY_PARTNER_STATUS_ACTIVE,
  UTILITY_PARTNER_STATUS_CHURNED,
  UTILITY_PARTNER_STATUS_ARCHIVED,
} from 'common/utility/constants';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import { SR_API_ACTION_UTILITY_PARTNER_UPDATE_STATUS } from 'api/utilityActionTypes';

import SrCard, {
  SR_CARD_BULLET_COLOR_BLUE,
  SR_CARD_CONTENT_TYPE_FORM_GRID,
} from 'client/components/SrCard/SrCard';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import { SCREEN_ID_UTILITY_PARTNER_SETTINGS_PARTNER } from 'client/constants/screenIds';

import requestUtilityApi from 'client/actions/requestUtilityApi';
import UtilityPartnerActions from 'client/actions/UtilityPartner';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedUtilityRoles, { getUserLandingPage } from 'client/helpers/allowedUtilityRoles';
import branch from 'client/core/branch';

import SrBox from 'client/components/SrBox';
import SrLabel from 'client/components/SrLabel/SrLabel';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrButton from 'client/components/SrButton/SrButton';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

const STATUS_OPTIONS = [
  { label: 'Pending Demo', value: UTILITY_PARTNER_STATUS_PENDING_DEMO },
  { label: 'Onboarded', value: UTILITY_PARTNER_STATUS_ONBOARDED },
  { label: 'Active', value: UTILITY_PARTNER_STATUS_ACTIVE },
  { label: 'Churned', value: UTILITY_PARTNER_STATUS_CHURNED },
  { label: 'Archived', value: UTILITY_PARTNER_STATUS_ARCHIVED },
];

const getFormFields = ({ isAdmin, currentStatus }) => {
  // Always include Churned and Archived as available options
  const availableOptions = [
    { label: 'Churned', value: UTILITY_PARTNER_STATUS_CHURNED },
    { label: 'Archived', value: UTILITY_PARTNER_STATUS_ARCHIVED },
  ];

  // If current status is not Churned or Archived, add it to the options
  if (
    currentStatus &&
    currentStatus !== UTILITY_PARTNER_STATUS_CHURNED &&
    currentStatus !== UTILITY_PARTNER_STATUS_ARCHIVED
  ) {
    const currentStatusOption = STATUS_OPTIONS.find((option) => option.value === currentStatus);
    if (currentStatusOption) {
      availableOptions.unshift(currentStatusOption); // Add at the beginning
    }
  }

  return [
    {
      id: 'status',
      title: 'Status',
      type: 'select',
      properties: {
        placeholder: 'Select a status',
        choices: availableOptions,
        disabled: !isAdmin,
      },
      validations: ['required'],
    },
  ];
};

const getChurnFields = ({ isAdmin }) => {
  return [
    {
      id: 'churnDate',
      title: 'Churn Date',
      type: 'date',
      properties: {
        placeholder: 'Select churn date',
        disabled: !isAdmin,
      },
      validations: ['required'],
    },
    {
      id: 'churnReason',
      title: 'Churn Reason',
      type: 'long_text',
      properties: {
        placeholder: 'Enter reason for churning',
        disabled: !isAdmin,
      },
      validations: ['required'],
    },
  ];
};

@withTransitionHook
@allowedUtilityRoles({
  roles: [UTILITY_USER_ROLE_ADMIN],
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_UTILITY_PARTNER_SETTINGS_PARTNER)
@withScrollToTop
@branch(({ match }) => ({
  utilityPartner: ['utilityPartners', 'bySlug', match.params.partnerSlug],
  utilityUser: ['utilityUser'],
}))
class UtilityPartnerSettingsPartnerPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Partner Settings',
  });

  static fetchData(match, inject) {
    const { partnerSlug } = match.params;
    const utilityPartner = inject.state.get(['utilityPartners', 'bySlug', partnerSlug]);

    const promises = [];

    if (!utilityPartner) {
      promises.push(
        UtilityPartnerActions.getBySlug(inject, {
          slug: partnerSlug,
        }),
      );
    }

    return Promise.all(promises);
  }

  static propTypes = {
    utilityPartner: PropTypes.object,
    utilityUser: PropTypes.object,
    isTransitioning: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      shouldShowSavedModal: false,
      errorMessage: null,
    };
    this.redirectTimeoutId = null;
  }

  componentWillUnmount() {
    if (this.redirectTimeoutId) {
      clearTimeout(this.redirectTimeoutId);
    }
  }

  _onSubmitForm = (formValues, formikBag) => {
    const { utilityPartner, dispatch, history } = this.props;

    this.setState({ errorMessage: null });

    const payload = {
      partnerId: utilityPartner.id,
      status: formValues.status,
    };

    if (formValues.status === UTILITY_PARTNER_STATUS_CHURNED) {
      payload.churnDate = formValues.churnDate
        ? moment(formValues.churnDate).format('YYYY-MM-DD')
        : null;
      payload.churnReason = formValues.churnReason;
    }

    return new Promise((resolve, reject) => {
      dispatch(requestUtilityApi, {
        action: SR_API_ACTION_UTILITY_PARTNER_UPDATE_STATUS,
        payload,
        onError: (error) => {
          this.setState({
            errorMessage: error.message,
          });
          formikBag.setSubmitting(false);
          reject(error);
        },
        onSuccess: () => {
          dispatch(UtilityPartnerActions.getBySlug, {
            slug: utilityPartner.slug,
            forceRefresh: true,
          }).then(() => {
            this.setState({ shouldShowSavedModal: true });

            this.redirectTimeoutId = setTimeout(() => {
              this.setState({ shouldShowSavedModal: false });
              history.push(`/utility-profit/${utilityPartner.slug}/settings`);
            }, 1500);

            formikBag.setSubmitting(false);
            resolve();
          });
        },
      });
    });
  };

  render() {
    const { utilityPartner, utilityUser, isTransitioning } = this.props;

    const { shouldShowSavedModal, errorMessage } = this.state;

    if (isTransitioning) {
      return null;
    }

    if (!utilityPartner) {
      const userLandingPage = getUserLandingPage({
        userRoles: utilityUser?.roles || [],
      });
      return <Redirect to={userLandingPage} />;
    }

    const isAdmin = hasRoleAdmin(utilityUser?.roles || []);
    const formFields = getFormFields({ isAdmin, currentStatus: utilityPartner.status });

    return (
      <SrPartnerPageV2
        backUrl={`/utility-profit/${utilityPartner.slug}/settings`}
        navbarTitle={utilityPartner.name}
        navbarImage={utilityPartner.logoUrl}
        redirectToLastLocation={false}
        pageHeader="Partner Settings"
      >
        <SrBox marginBottom="100px">
          <Formik
            enableReinitialize
            onSubmit={this._onSubmitForm}
            initialValues={createInitialValues({
              formFields: [
                ...getFormFields({ isAdmin, currentStatus: utilityPartner.status }),
                ...getChurnFields({ isAdmin }),
              ],
              defaultValues: {
                status: utilityPartner.status,
                churnDate: utilityPartner.churnDate || null,
                churnReason: utilityPartner.churnReason || '',
              },
            })}
            validate={(values) => {
              const fieldsToValidate =
                values.status === UTILITY_PARTNER_STATUS_CHURNED
                  ? [...formFields, ...getChurnFields({ isAdmin })]
                  : formFields;
              return createFormValidationFn(fieldsToValidate)(values);
            }}
            render={(formikBag) => {
              return (
                <SrBox marginBottom="100px">
                  <Form>
                    <SrCard
                      color={SR_CARD_BULLET_COLOR_BLUE}
                      contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                      headerFlexDirection="column"
                      header={
                        <SrBox display="flex" alignItems="center" columnGap="12px">
                          <span>Partner Status</span>
                          <SrLabel
                            borderColor="#152c5b"
                            borderWidth="1"
                            color="blueNavy"
                            fontSize="small"
                            fontWeight="500"
                            padding="4px 12px"
                            label="UP Admin"
                          />
                        </SrBox>
                      }
                      subheader="Update the status of this partner in the system."
                    >
                      <SrBox
                        display="flex"
                        alignItems="flex-end"
                        justifyContent="space-between"
                        columnGap="16px"
                      >
                        <SrBox flex="1 0 auto" maxWidth={300}>
                          {formFields.map((formField) => (
                            <SrFormikField
                              key={formField.id}
                              formField={formField}
                              formikBag={formikBag}
                              minWidth={300}
                              theme={SR_FIELD_THEME_ADOBE}
                            />
                          ))}
                          {formikBag.values.status === UTILITY_PARTNER_STATUS_CHURNED &&
                            getChurnFields({ isAdmin }).map((formField) => (
                              <SrFormikField
                                key={formField.id}
                                formField={formField}
                                formikBag={formikBag}
                                minWidth={300}
                                theme={SR_FIELD_THEME_ADOBE}
                              />
                            ))}
                        </SrBox>
                        {isAdmin && (
                          <SrBox>
                            <SrButton
                              type="submit"
                              disabled={formikBag.isSubmitting}
                              backgroundColor="green"
                              textColor="white"
                            >
                              Save
                            </SrButton>
                          </SrBox>
                        )}
                      </SrBox>

                      {errorMessage && (
                        <SrBox marginTop="20px">
                          <SrErrorMessage>{errorMessage}</SrErrorMessage>
                        </SrBox>
                      )}
                    </SrCard>
                  </Form>
                </SrBox>
              );
            }}
          />
        </SrBox>
        {shouldShowSavedModal && (
          <SrSimpleMessageModal
            iconUrl="/images/checkmark-circle-large-green.svg"
            headline="Saved!"
            maxWidth={400}
            minWidth={400}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          />
        )}
      </SrPartnerPageV2>
    );
  }
}

UtilityPartnerSettingsPartnerPage.displayName = 'UtilityPartnerSettingsPartnerPage';

export default UtilityPartnerSettingsPartnerPage;
